/**
 * cacheAtoms - Atoms for query result caching, exploration graph derivation,
 * focus snapshots, explored-node indicators, and the query context generation.
 *
 * These atoms persist across focus/unfocus cycles to enable:
 * - Cache hits for previously-fetched discovery/node queries
 * - Breadcrumb path computation via the derived exploration graph
 * - Snapshot restoration when re-focusing a previously-focused node
 * - Purple dot indicators on nodes that have been explored
 */

import { atom, type PrimitiveAtom } from 'jotai';

import type { ObjectNodeData } from '../nodes/ObjectNode';
import { type CachedQueryResult, type RelationshipInfo } from '../services/cypherQueryService';
import type { SelectedRelationship } from './focusAtoms';

// ============================================================================
// Types
// ============================================================================

/**
 * Per-node snapshot of focus state. When a user removes focus and later
 * re-focuses the same node, the snapshot is restored so they see the
 * same selected relationships, drill state, etc.
 */
export interface FocusSnapshot {
	selectedRelationships: SelectedRelationship[];
	navigationPath: ObjectNodeData[];
	activeNode: ObjectNodeData | null;
}

// ============================================================================
// Source atoms
// ============================================================================

/**
 * Query result cache. Keyed by composite key:
 * - `${nodeAri}:${direction}:${relType}` for node loading results
 * - `${nodeAri}:discover:${direction}` for discovery results
 */
export const QueryCacheAtom: PrimitiveAtom<Map<string, CachedQueryResult>> & {
	init: Map<string, CachedQueryResult>;
} = atom(new Map<string, CachedQueryResult>());

/**
 * Set of node ARIs that have been explored (have focus mode data).
 * Used to show a purple dot indicator on nodes that have exploration data.
 */
export const ExploredNodeArisAtom: PrimitiveAtom<Set<string>> & {
	init: Set<string>;
} = atom(new Set<string>());

/**
 * Per-node snapshots of focus state, keyed by the focused node's ARI.
 * Restored when re-focusing a previously-focused node.
 */
export const FocusSnapshotsAtom: PrimitiveAtom<Map<string, FocusSnapshot>> & {
	init: Map<string, FocusSnapshot>;
} = atom(new Map<string, FocusSnapshot>());

// ============================================================================
// In-flight discovery deduplication
// ============================================================================

/**
 * Result of an in-flight discovery promise. Contains the discovered
 * relationship info for both directions.
 */
export interface InflightDiscoveryResult {
	outgoing: RelationshipInfo[];
	incoming: RelationshipInfo[];
}

/**
 * In-flight discovery promises, keyed by node ARI.
 *
 * When a discovery request is in-flight for a node, its promise is stored
 * here. If another caller requests discovery for the same node before the
 * first resolves, the existing promise is reused instead of launching
 * duplicate HTTP requests. The entry is removed in a `finally` block once
 * the promise settles (resolves or rejects).
 */
export const InflightDiscoveryAtom: PrimitiveAtom<Map<string, Promise<InflightDiscoveryResult>>> & {
	init: Map<string, Promise<InflightDiscoveryResult>>;
} = atom(new Map<string, Promise<InflightDiscoveryResult>>());

// ============================================================================
// Query context generation
// ============================================================================

/**
 * Incremented whenever the effective X-Query-Context changes. The cache keys
 * above are context-free, so async work captures this before fetching and,
 * if it changed, returns without writing caches or panel state (see
 * `InvalidateQueryContextCachesAtom`).
 */
export const QueryContextGenerationAtom: PrimitiveAtom<number> & {
	init: number;
} = atom(0);

/** The X-Query-Context the caches above were built under; null until first recorded. */
export const ExplorationQueryContextAtom: PrimitiveAtom<string | null> & {
	init: string | null;
} = atom<string | null>(null);
